/**
 * JsGameSandbox — hosts an AI-generated JavaScript game inside a
 * sandboxed iframe and bridges its lifecycle events to the parent.
 */

import { useEffect, useMemo, useRef, useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Trophy, AlertTriangle } from "lucide-react";

import type { JsGameConfig } from "@/types/jsGame";
import type { GameRunResult, SceneAttempt } from "@/types/gameSpec";
import { GameMessageSchema, type GameMessage } from "./messageSchema";
import { buildSandboxSrcdoc } from "./sandboxTemplate";
import { trackEvent } from "@/lib/analytics/ga";

interface Props {
  config: JsGameConfig;
  gameId: string;
  grade: string | number;
  onComplete: (result: GameRunResult) => void;
  onExit?: () => void;
}

export const JsGameSandbox = ({ config, gameId, grade, onComplete, onExit }: Props) => {
  const iframeRef = useRef<HTMLIFrameElement | null>(null);
  const startedAt = useRef<number>(Date.now());
  const completedRef = useRef(false);

  const [score, setScore] = useState(0);
  const [maxScore, setMaxScore] = useState<number | null>(null);
  const [ready, setReady] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [runKey, setRunKey] = useState(0);

  const srcdoc = useMemo(
    () =>
      buildSandboxSrcdoc({
        library: config.library,
        code: config.code,
        grade,
        difficulty: config.meta.difficulty,
        theme: config.meta.theme,
      }),
    [config, grade, runKey],
  );

  useEffect(() => {
    trackEvent("game_play_started", { game_id: gameId, grade: String(grade) });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const onMsg = (ev: MessageEvent) => {
      if (!iframeRef.current || ev.source !== iframeRef.current.contentWindow) return;
      const parsed = GameMessageSchema.safeParse(ev.data);
      if (!parsed.success) return;
      handleMessage(parsed.data);
    };
    window.addEventListener("message", onMsg);
    return () => window.removeEventListener("message", onMsg);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [runKey]);

  // 90s timeout if the game never reports ready
  useEffect(() => {
    const t = setTimeout(() => {
      if (!ready && !error) setError("Game took too long to load.");
    }, 90_000);
    return () => clearTimeout(t);
  }, [ready, error, runKey]);

  const handleMessage = (msg: GameMessage) => {
    const m = msg as Record<string, any>;
    switch (m.type) {
      case "ready":
        setReady(true);
        return;
      case "score":
        setScore(Number(m.score));
        if (typeof m.max === "number") setMaxScore(m.max);
        return;
      case "complete":
        finish({
          score: Number(m.score),
          max: Number(m.max),
          won: Boolean(m.won),
          time_ms: typeof m.time_ms === "number" ? m.time_ms : undefined,
          stats: m.stats,
        });
        return;
      case "error":
        setError(String(m.message));
        return;
      case "log":
        // eslint-disable-next-line no-console
        console.log("[game]", m.message);
        return;
    }
  };

  const finish = (msg: { score: number; max: number; won: boolean; time_ms?: number; stats?: Record<string, unknown> }) => {
    if (completedRef.current) return;
    completedRef.current = true;

    // Build a minimal SceneAttempt list so legacy ResultScreen still works.
    const attempt: SceneAttempt = {
      scene_id: "js-runtime",
      scene_index: 0,
      scene_type: "mcq", // placeholder — js-runtime games are atomic
      is_correct: msg.won,
      points_awarded: msg.score,
      time_taken_ms: msg.time_ms ?? Date.now() - startedAt.current,
      details: msg.stats,
    };

    const result: GameRunResult = {
      spec_version: 1,
      score: msg.score,
      max_score: msg.max,
      won: msg.won,
      scene_count: 1,
      scenes_correct: msg.won ? 1 : 0,
      streak_max: 0,
      time_spent_ms: msg.time_ms ?? Date.now() - startedAt.current,
      attempts: [attempt],
    };

    trackEvent("game_play_completed", {
      game_id: gameId,
      score: msg.score,
      won: msg.won,
      time_spent: Math.round(result.time_spent_ms / 1000),
    });

    onComplete(result);
  };

  const restart = () => {
    completedRef.current = false;
    setScore(0);
    setMaxScore(null);
    setReady(false);
    setError(null);
    startedAt.current = Date.now();
    setRunKey((k) => k + 1);
  };

  if (error) {
    return (
      <Card>
        <CardContent className="p-6 text-center space-y-3">
          <AlertTriangle className="mx-auto h-10 w-10 text-destructive" />
          <h3 className="text-lg font-semibold">This game couldn't run</h3>
          <p className="text-sm text-muted-foreground">{error}</p>
          <div className="flex justify-center gap-2 pt-2">
            <Button onClick={restart}>Try again</Button>
            {onExit && (
              <Button variant="outline" onClick={onExit}>
                Exit
              </Button>
            )}
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-3">
      <Card>
        <CardContent className="p-3 flex flex-wrap items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <Badge variant="secondary">{config.library}</Badge>
            <span className="text-sm font-medium truncate max-w-[40ch]">{config.meta.title}</span>
          </div>
          <span className="flex items-center gap-1 text-sm">
            <Trophy className="h-4 w-4" />
            {score}
            {maxScore != null ? ` / ${maxScore}` : ""}
          </span>
        </CardContent>
      </Card>

      <div className="rounded-lg overflow-hidden border bg-black" style={{ aspectRatio: "16 / 10" }}>
        <iframe
          key={runKey}
          ref={iframeRef}
          title={config.meta.title}
          srcDoc={srcdoc}
          sandbox="allow-scripts"
          className="w-full h-full"
          // referrerpolicy + no-allow-same-origin = generated code is fully isolated
          referrerPolicy="no-referrer"
        />
      </div>

      {onExit && (
        <div className="flex justify-end">
          <Button variant="ghost" size="sm" onClick={onExit}>
            Exit game
          </Button>
        </div>
      )}
    </div>
  );
};

export default JsGameSandbox;
